import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Tooltip,
  IconButton,
  Chip,
} from '@mui/material';
import {
  AttachMoney,
  ShoppingCart,
  TrendingDown,
  Speed,
  ShowChart,
  InfoOutlined,
  CheckCircle,
} from '@mui/icons-material';
import { formatCurrency } from '../../utils/formatters';
import StatCard from './StatCard';

const NetPositiveBadge = () => (
  <Box>
    <Chip
      icon={<CheckCircle />}
      label="Net-positive"
      color="success"
      size="small"
      sx={{ fontWeight: 600 }}
    />
    <Tooltip
      title="You earned more in affiliate revenue from orders sent to partners than you spent acquiring orders. Handoff is generating revenue for you at no net cost."
      arrow
    >
      <IconButton size="small" sx={{ padding: 0, ml: 0.5 }}>
        <InfoOutlined sx={{ fontSize: 16 }} />
      </IconButton>
    </Tooltip>
  </Box>
);

const HeroKPIs = ({ data }) => {
  if (!data) return null;

  const isNetPositive = data.roas === null || data.roas === undefined;

  return (
    <Grid container spacing={{ xs: 1.5, sm: 2 }}>
      <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
        <StatCard
          title="Revenue generated"
          value={formatCurrency(data.revenue_generated)}
          icon={<AttachMoney />}
          color="success.main"
          tooltip="Total order revenue from orders received from partners."
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
        <StatCard
          title="Orders acquired"
          value={data.orders_acquired || 0}
          icon={<ShoppingCart />}
          color="primary.main"
          tooltip="Orders placed on your site by visitors referred from partner sites."
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
        <StatCard
          title="Net acquisition cost"
          value={formatCurrency(data.net_acquisition_cost)}
          icon={<TrendingDown />}
          color="warning.main"
          tooltip="Commission paid to partners minus affiliate revenue earned. Your true cost of acquiring orders through Handoff."
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
        <StatCard
          title="Cost per order"
          value={formatCurrency(data.cpo)}
          icon={<Speed />}
          color="info.main"
          tooltip="Your net acquisition cost divided by the number of orders acquired through Handoff."
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
        <Card>
          <CardContent sx={{ py: { xs: 1.5, sm: 2 }, px: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
              <Box sx={{ color: 'success.main', opacity: 0.7, display: 'flex' }}>
                <ShowChart />
              </Box>
              <Typography color="textSecondary" variant="body2">
                ROAS
              </Typography>
              <Tooltip title="Revenue generated divided by net acquisition cost. Shows how much revenue each euro of acquisition cost generates." arrow>
                <IconButton size="small" sx={{ padding: 0 }}>
                  <InfoOutlined sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
            {isNetPositive ? (
              <NetPositiveBadge />
            ) : (
              <Typography variant="h5" component="div" color="success.main" sx={{ fontWeight: 600 }}>
                {data.roas}x
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default HeroKPIs;
